import React from 'react';

import { useAccount, useUser } from '~/apis';
import { selectGameType, selectUserId, useSelector } from '~/app/selectors';
import { GameType } from '~/server/src/dtos';

import ProfilePicture from './ProfilePicture';

interface PlayerProfilePictureProps {
  userId: string;
  username: string;
  /**
   * Already-known profile picture for this user (e.g. from a toplist row that
   * carries the full user object). When provided, the component skips the
   * per-user `GET /users/:userId` fetch — pass it from list/table cells to avoid
   * an N+1 request fan-out on render.
   */
  profilePicture?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const PlayerProfilePicture: React.FC<PlayerProfilePictureProps> = ({
  userId,
  username,
  profilePicture: profilePictureProp,
  size = 'md',
  className,
}) => {
  const gameType = useSelector(selectGameType);
  const currentUserId = useSelector(selectUserId);
  const isLocalMode = gameType === GameType.Training;
  const { data: account } = useAccount();

  // Only fetch when the caller hasn't already supplied the picture. In local mode,
  // only fetch for the current user, otherwise fetch for all users (including bots).
  const shouldFetchUser =
    profilePictureProp === undefined &&
    (isLocalMode ? userId === currentUserId && userId !== '' : userId !== '');

  const { data: user } = useUser(userId, {
    enabled: shouldFetchUser,
  });

  // In training mode, if this is the current user and we have account data, use the account data
  // This ensures we use the actual user's profile picture when logged in
  const isCurrentUserInTraining = isLocalMode && userId === currentUserId && userId !== '';
  const profilePicture =
    isCurrentUserInTraining && account?.profilePicture
      ? account.profilePicture
      : (profilePictureProp ?? user?.profilePicture ?? undefined);

  return (
    <ProfilePicture
      username={username}
      size={size}
      className={className}
      profilePicture={profilePicture}
      userId={userId}
      isLocalMode={isLocalMode}
    />
  );
};

export default PlayerProfilePicture;
